import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import CredibilityStrip from "@/components/funnel/CredibilityStrip";
import GrowthCheck from "@/components/funnel/GrowthCheck";
import VTSCards from "@/components/funnel/VTSCards";
import StickyMobileCTA from "@/components/funnel/StickyMobileCTA";
import ResultsSection from "@/components/ResultsSection";
import ProcessSection from "@/components/ProcessSection";
import FounderSection from "@/components/FounderSection";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";
import { useEffect } from "react";
import { updateOpenGraph } from "@/lib/seo";

const HOMEPAGE_TITLE = "Dental Marketing Agency in Florida | N. Santiago Media";
const HOMEPAGE_DESC = "N. Santiago Media helps dental clinics grow with visibility, trust, and patient conversion systems. Dental marketing for local search and reputation.";

const structuredData = {
  "@context": "https://schema.org",
  "@type": ["MarketingAgency", "LocalBusiness"],
  "name": "N. Santiago Media",
  "url": "https://www.nsantiagomedia.com",
  "telephone": "+17722022664",
  "email": "info@nsantiagomedia.com",
  "description": "Dental marketing agency in Florida. N. Santiago Media helps dental clinics grow through visibility, trust, and patient conversion systems — local SEO, Google Business Profile, reviews, video, and practice-level workflows.",
  "image": "https://www.nsantiagomedia.com/images/logo-white.png",
  "logo": "https://www.nsantiagomedia.com/images/logo-white.png",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Port St. Lucie",
    "addressRegion": "FL",
    "addressCountry": "US"
  },
  "geo": { "@type": "GeoCoordinates", "latitude": 27.2730, "longitude": -80.3582 },
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "18:00" }
  ],
  "priceRange": "$$",
  "areaServed": [
    { "@type": "City", "name": "Port St. Lucie" },
    { "@type": "City", "name": "Stuart" },
    { "@type": "City", "name": "Jensen Beach" },
    { "@type": "City", "name": "Palm City" },
    { "@type": "City", "name": "Fort Pierce" },
    { "@type": "City", "name": "Vero Beach" },
    { "@type": "City", "name": "Jupiter" },
    { "@type": "City", "name": "West Palm Beach" },
    { "@type": "State", "name": "Florida" },
  ],
  "founder": {
    "@type": "Person",
    "name": "Nathaniel Santiago",
    "sameAs": [
      "https://www.instagram.com/nsantiagomedia",
      "https://www.youtube.com/@nsantiagomedia",
      "https://www.facebook.com/nsantiagomedia"
    ]
  },
  "knowsAbout": [
    "Dental Marketing",
    "Dental SEO",
    "Google Business Profile for Dentists",
    "Dental Patient Acquisition",
    "Dental Video Marketing",
    "Dental Reputation Management",
    "Patient Conversion Systems",
    "Local SEO",
    "AI Search Optimization"
  ],
  "sameAs": [
    "https://www.instagram.com/nsantiagomedia",
    "https://www.youtube.com/@nsantiagomedia",
    "https://www.facebook.com/nsantiagomedia",
    "https://www.tiktok.com/@n.santiagomedia"
  ]
};

const websiteStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "N. Santiago Media",
  "url": "https://www.nsantiagomedia.com",
  "publisher": { "@type": "Organization", "name": "N. Santiago Media" },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://www.nsantiagomedia.com/insights?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How can marketing help my dental clinic grow?",
      "acceptedAnswer": { "@type": "Answer", "text": "Marketing helps your dental clinic show up when patients search on Google, build trust through reviews and content, and turn interest into scheduled appointments. A focused system replaces inconsistent referrals with a predictable flow of new patients." }
    },
    {
      "@type": "Question",
      "name": "What makes N. Santiago Media different from other dental marketing agencies?",
      "acceptedAnswer": { "@type": "Answer", "text": "We combine dental practice operations experience with marketing strategy. Most agencies focus on deliverables. We focus on patient flow — visibility, trust, and conversion. Every system we build is designed to help your clinic turn attention into scheduled appointments." }
    },
    {
      "@type": "Question",
      "name": "Do you only work with dental clinics?",
      "acceptedAnswer": { "@type": "Answer", "text": "Our primary focus is dental clinics. We also support select local service-based businesses that need stronger visibility, trust, and conversion systems." }
    },
    {
      "@type": "Question",
      "name": "How long does it take to see results?",
      "acceptedAnswer": { "@type": "Answer", "text": "It depends on where your clinic is starting. Paid campaigns can generate interest quickly. Local SEO and reputation systems typically show measurable improvements within 3–6 months, with results compounding as visibility and trust grow. We set clear expectations during your audit." }
    },
    {
      "@type": "Question",
      "name": "What is included in a Dental Growth Audit?",
      "acceptedAnswer": { "@type": "Answer", "text": "A no-obligation review of your Google presence, website, reviews, content, competitor positioning, and patient journey. We identify where visibility, trust, or conversion is breaking down and give you a clear plan to fix it, whether you work with us or not." }
    }
  ]
};

const Index = () => {
  useEffect(() => {
    document.title = HOMEPAGE_TITLE;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) metaDesc.setAttribute("content", HOMEPAGE_DESC);
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) canonical.href = "https://www.nsantiagomedia.com";

    updateOpenGraph({
      title: HOMEPAGE_TITLE,
      description: HOMEPAGE_DESC,
      url: "https://www.nsantiagomedia.com",
      type: "website",
    });

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify([structuredData, websiteStructuredData, faqStructuredData]);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
      if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <HeroSection />
        <CredibilityStrip />
        <GrowthCheck />
        <VTSCards />
        <ResultsSection />
        <FounderSection />
        <ProcessSection />
        <FAQSection />
        <CTASection />
      </main>
      <StickyMobileCTA />
      <Footer />
    </div>
  );
};

export default Index;
